import PropTypes from 'prop-types';
import { useMemo, useEffect } from 'react';
import { useMatrixClient } from '../../../../../context/MatrixContext';
import EmojiPickerReact from 'emoji-picker-react';
import './SmartEmojiPicker.css';

const SmartEmojiPicker = ({
  messageId,
  reactions = new Map(), // All reactions for this message
  onToggleReaction,
  onClose = () => {},
}) => {
  const client = useMatrixClient();
  const currentUserId = client?.getUserId();

  // Get current user's reactions for highlighting
  const currentUserReactions = useMemo(() => {
    const userReactions = new Map();
    
    reactions.forEach((reactors, emoji) => {
      const userReaction = reactors.find(reactor => reactor.senderId === currentUserId);
      if (userReaction) {
        userReactions.set(emoji, userReaction);
      }
    });
    
    return userReactions;
  }, [reactions, currentUserId]);

  const handleEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
    
    // Check if user has already reacted with this emoji
    const existingReaction = currentUserReactions.get(emoji);
    
    if (existingReaction) {
      // User has already reacted - toggle it off (remove)
      onToggleReaction(messageId, emoji, existingReaction.eventId);
    } else {
      // User hasn't reacted - add new reaction
      onToggleReaction(messageId, emoji, null);
    }
    
    onClose();
  };

  // Our custom reactions - simple array of unified emoji ids
  const quickReactions = [
    '👍', // thumbs up
    '👎', // thumbs down  
    '🙌', // raised hands
    '👏', // clapping
    '🤝', // handshake
    '😊', // smile
  ];

  // Add CSS styling to highlight selected emojis in the picker
  useEffect(() => {
    const highlightSelectedEmojis = () => {
      // Add a small delay to ensure the emoji picker has rendered
      setTimeout(() => {
        const emojiButtons = document.querySelectorAll('.smart-emoji-picker .EmojiPickerReact button');
        
        emojiButtons.forEach((button) => {
          const emojiSpan = button.querySelector('span');
          if (emojiSpan) {
            const emoji = emojiSpan.textContent;
            const isSelected = currentUserReactions.has(emoji);
            
            if (isSelected) {
              button.classList.add('user-selected-emoji');
              button.title = `Remove ${emoji} reaction`;
            } else {
              button.classList.remove('user-selected-emoji');
              button.title = `Add ${emoji} reaction`;
            }
          }
        });
      }, 100);
    };

    highlightSelectedEmojis();
    
    // Re-run when reactions change
    const interval = setInterval(highlightSelectedEmojis, 500);
    
    return () => clearInterval(interval);
  }, [currentUserReactions]);

  return (
    <div className="smart-emoji-picker">
      {/* Emoji picker with smart highlighting */}
      <div className="emoji-picker-section">
        <EmojiPickerReact
          onEmojiClick={handleEmojiClick}
          previewConfig={{ showPreview: false }}
          lazyLoadEmojis={true}
          emojiStyle='native'
          reactionsDefaultOpen={true}
          allowExpandReactions={true}
          reactions={quickReactions.map(emoji => {
            // Convert to unified format if needed for emoji-picker-react
            return emoji.codePointAt(0).toString(16);
          })}
        />
      </div>
    </div>
  );
};

SmartEmojiPicker.propTypes = {
  messageId: PropTypes.string.isRequired,
  reactions: PropTypes.instanceOf(Map),
  onToggleReaction: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

export default SmartEmojiPicker;